// Espelha validateMetaTemplatePayload do backend para fornecer feedback inline no cliente.

import { extractVarIndices, isSequentialFromOne } from "./templatePayload";

const NAME_REGEX = /^[a-z0-9_]+$/;
const VAR_ANYWHERE_REGEX = /\{\{[^}]+\}\}/;
const STARTS_WITH_VAR_REGEX = /^\s*\{\{[^}]+\}\}/;
const ENDS_WITH_VAR_REGEX = /\{\{[^}]+\}\}\s*$/;
const EMOJI_REGEX = /[\u{1F300}-\u{1F6FF}\u{1F900}-\u{1F9FF}\u{2600}-\u{27BF}\u{1F000}-\u{1F02F}\u{1F0A0}-\u{1F0FF}\u{1F100}-\u{1F1FF}]/u;

export const MAX_BODY_LENGTH = 1024;
export const MAX_HEADER_LENGTH = 60;
export const MAX_FOOTER_LENGTH = 60;
export const MAX_BUTTON_TEXT_LENGTH = 25;
export const MAX_BUTTONS = 10;
export const VALID_CATEGORIES = ["UTILITY", "MARKETING", "AUTHENTICATION"];
export const VALID_HEADER_FORMATS = ["TEXT", "IMAGE", "VIDEO", "DOCUMENT"];
export const VALID_BUTTON_TYPES = ["QUICK_REPLY", "URL", "PHONE_NUMBER", "COPY_CODE"];

/**
 * Retorna { errors: string[], fieldErrors: { name?, body?, header?, footer?, buttons: { [idx]: string } } }
 */
export function validateForm(form, mode = "create") {
  const errors = [];
  const fieldErrors = { buttons: {} };

  if (mode === "create") {
    const name = (form.name || "").trim();
    if (!name) {
      const msg = "Nome do template é obrigatório.";
      errors.push(msg);
      fieldErrors.name = msg;
    } else if (!NAME_REGEX.test(name)) {
      const msg = "Use apenas minúsculas, números e underscore.";
      errors.push(`Nome: ${msg}`);
      fieldErrors.name = msg;
    }
    if (!form.language || !String(form.language).trim()) {
      errors.push("Idioma é obrigatório.");
    }
  }

  if (!VALID_CATEGORIES.includes((form.category || "").toUpperCase())) {
    errors.push("Categoria inválida.");
  }

  // BODY
  const bodyText = form.body?.text || "";
  if (!bodyText.trim()) {
    const msg = "Corpo da mensagem é obrigatório.";
    errors.push(msg);
    fieldErrors.body = msg;
  } else {
    if (bodyText.length > MAX_BODY_LENGTH) {
      const msg = `Máximo ${MAX_BODY_LENGTH} caracteres.`;
      errors.push(`BODY: ${msg}`);
      fieldErrors.body = msg;
    }
    if (STARTS_WITH_VAR_REGEX.test(bodyText)) {
      const msg = "Não pode começar com variável.";
      errors.push(`BODY: ${msg}`);
      fieldErrors.body = msg;
    }
    if (ENDS_WITH_VAR_REGEX.test(bodyText)) {
      const msg = "Não pode terminar com variável.";
      errors.push(`BODY: ${msg}`);
      fieldErrors.body = msg;
    }
    const indices = extractVarIndices(bodyText);
    if (indices.length > 0 && !isSequentialFromOne(indices)) {
      const msg = "Variáveis devem ser sequenciais começando em {{1}}.";
      errors.push(`BODY: ${msg}`);
      fieldErrors.body = msg;
    }
    indices.forEach(idx => {
      if (!String(form.body?.examples?.[idx] || "").trim()) {
        errors.push(`BODY: exemplo da variável {{${idx}}} é obrigatório.`);
      }
    });
  }

  // HEADER
  if (form.header?.enabled) {
    const fmt = (form.header.format || "TEXT").toUpperCase();
    if (!VALID_HEADER_FORMATS.includes(fmt)) {
      errors.push("HEADER: formato inválido.");
    } else if (fmt === "TEXT") {
      const text = form.header.text || "";
      if (!text.trim()) {
        const msg = "Texto do cabeçalho é obrigatório.";
        errors.push(`HEADER: ${msg}`);
        fieldErrors.header = msg;
      } else {
        if (text.length > MAX_HEADER_LENGTH) {
          const msg = `Máximo ${MAX_HEADER_LENGTH} caracteres.`;
          errors.push(`HEADER: ${msg}`);
          fieldErrors.header = msg;
        }
        if (EMOJI_REGEX.test(text)) {
          const msg = "Não pode conter emojis.";
          errors.push(`HEADER: ${msg}`);
          fieldErrors.header = msg;
        }
        if (/\n/.test(text)) {
          const msg = "Não pode conter quebras de linha.";
          errors.push(`HEADER: ${msg}`);
          fieldErrors.header = msg;
        }
        const indices = extractVarIndices(text);
        if (indices.length > 1) {
          const msg = "Permite no máximo 1 variável.";
          errors.push(`HEADER: ${msg}`);
          fieldErrors.header = msg;
        }
        if (indices.length === 1) {
          if (indices[0] !== 1) {
            const msg = "A variável deve ser {{1}}.";
            errors.push(`HEADER: ${msg}`);
            fieldErrors.header = msg;
          }
          if (!String(form.header.examples?.[1] || "").trim()) {
            errors.push("HEADER: exemplo da variável {{1}} é obrigatório.");
          }
        }
      }
    } else {
      // IMAGE/VIDEO/DOCUMENT
      if (
        !form.header.media?.resumableHandle &&
        !form.header.media?.localUrl &&
        !form.header.media?.mediaMetaId
      ) {
        const msg = "Faça upload da mídia do cabeçalho.";
        errors.push(`HEADER ${fmt}: ${msg}`);
        fieldErrors.header = msg;
      }
    }
  }

  // FOOTER
  if (form.footer?.enabled) {
    const text = form.footer.text || "";
    if (text.length > MAX_FOOTER_LENGTH) {
      const msg = `Máximo ${MAX_FOOTER_LENGTH} caracteres.`;
      errors.push(`FOOTER: ${msg}`);
      fieldErrors.footer = msg;
    }
    if (VAR_ANYWHERE_REGEX.test(text)) {
      const msg = "Não pode conter variáveis.";
      errors.push(`FOOTER: ${msg}`);
      fieldErrors.footer = msg;
    }
  }

  // BUTTONS
  if (form.buttons?.enabled) {
    const items = Array.isArray(form.buttons.items) ? form.buttons.items : [];
    if (items.length === 0) {
      errors.push("BUTTONS: adicione pelo menos um botão ou desative a seção.");
    }
    if (items.length > MAX_BUTTONS) {
      errors.push(`BUTTONS: máximo ${MAX_BUTTONS} botões.`);
    }
    items.forEach((b, i) => {
      const ref = `Botão ${i + 1}`;
      const type = (b.type || "").toUpperCase();
      if (!VALID_BUTTON_TYPES.includes(type)) {
        const msg = "Tipo inválido.";
        errors.push(`${ref}: ${msg}`);
        fieldErrors.buttons[i] = msg;
        return;
      }
      const text = (b.text || "").trim();
      if (!text) {
        const msg = "Texto é obrigatório.";
        errors.push(`${ref}: ${msg}`);
        fieldErrors.buttons[i] = msg;
        return;
      }
      if (text.length > MAX_BUTTON_TEXT_LENGTH) {
        const msg = `Texto excede ${MAX_BUTTON_TEXT_LENGTH} caracteres.`;
        errors.push(`${ref}: ${msg}`);
        fieldErrors.buttons[i] = msg;
      }
      if (VAR_ANYWHERE_REGEX.test(text)) {
        const msg = "Texto não pode conter variáveis.";
        errors.push(`${ref}: ${msg}`);
        fieldErrors.buttons[i] = msg;
      }
      if (type === "URL") {
        const url = (b.url || "").trim();
        if (!url) {
          const msg = "URL é obrigatória.";
          errors.push(`${ref}: ${msg}`);
          fieldErrors.buttons[i] = msg;
        } else if (!/^https?:\/\//i.test(url)) {
          const msg = "URL deve começar com http:// ou https://.";
          errors.push(`${ref}: ${msg}`);
          fieldErrors.buttons[i] = msg;
        } else if (url.includes("{{") && !String(b.example || "").trim()) {
          const msg = "Example obrigatório quando a URL tem variável.";
          errors.push(`${ref}: ${msg}`);
          fieldErrors.buttons[i] = msg;
        }
      } else if (type === "PHONE_NUMBER") {
        if (!String(b.phone_number || "").trim()) {
          const msg = "Telefone é obrigatório.";
          errors.push(`${ref}: ${msg}`);
          fieldErrors.buttons[i] = msg;
        }
      } else if (type === "COPY_CODE") {
        if (!String(b.example || "").trim()) {
          const msg = "Código a copiar é obrigatório.";
          errors.push(`${ref}: ${msg}`);
          fieldErrors.buttons[i] = msg;
        }
      }
    });
  }

  return { errors, fieldErrors, valid: errors.length === 0 };
}
